"use client";

import Script from "next/script";
import { useUser } from "@clerk/nextjs";
import { usePathname } from "next/navigation";

const FeedbackIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path fillRule="evenodd" clipRule="evenodd" d="M1.25 12C1.25 6.06294 6.06294 1.25 12 1.25C17.937 1.25 22.75 6.06293 22.75 12C22.75 17.937 17.937 22.75 12 22.75C10.1437 22.75 8.39536 22.2788 6.87016 21.4493L2.63727 22.2373C2.39422 22.2826 2.14448 22.2051 1.96967 22.0303C1.79485 21.8555 1.71742 21.6058 1.76267 21.3627L2.55076 17.1298C1.72113 15.6046 1.25 13.8563 1.25 12ZM10.3446 7.60313C10.0001 7.89541 9.75 8.34102 9.75 9.00001C9.75 9.41422 9.41421 9.75001 9 9.75001C8.58579 9.75001 8.25 9.41422 8.25 9.00001C8.25 7.90899 8.68736 7.04209 9.37414 6.45937C10.0446 5.89048 10.9119 5.625 11.75 5.625C12.5882 5.625 13.4554 5.89049 14.1259 6.45938C14.8126 7.0421 15.25 7.90899 15.25 9.00001C15.25 9.76589 15.0538 10.3495 14.7334 10.8301C14.4642 11.234 14.1143 11.5462 13.839 11.7919C13.8089 11.8187 13.7798 11.8447 13.7517 11.8699C13.4445 12.1464 13.213 12.3743 13.0433 12.6741C12.881 12.9609 12.75 13.3616 12.75 13.9999C12.75 14.4141 12.4142 14.7499 12 14.7499C11.5858 14.7499 11.25 14.4141 11.25 13.9999C11.25 13.1382 11.4315 12.4764 11.7379 11.9352C12.037 11.4069 12.4305 11.041 12.7483 10.755L12.8205 10.6901C13.1207 10.4204 13.3276 10.2347 13.4853 9.99803C13.6337 9.77553 13.75 9.48414 13.75 9.00001C13.75 8.34103 13.4999 7.89542 13.1554 7.60314C12.7946 7.29702 12.2868 7.125 11.75 7.125C11.2131 7.125 10.7054 7.29702 10.3446 7.60313ZM12.5672 18.501C12.8445 18.1933 12.8197 17.719 12.512 17.4418C12.2042 17.1646 11.73 17.1893 11.4528 17.497L11.4428 17.5081C11.1655 17.8159 11.1903 18.2901 11.498 18.5674C11.8058 18.8446 12.28 18.8199 12.5572 18.5121L12.5672 18.501Z" fill="currentColor" />
  </svg>
);

function useTallyProps() {
  const { isSignedIn, user } = useUser();
  const tallyId = isSignedIn ? "1AM8R4" : "NpLkEp";
  const email = user?.primaryEmailAddress?.emailAddress;
  const name = user?.firstName;

  const hiddenFields = [
    email && `email=${email}`,
    name && `name=${name}`,
  ].filter(Boolean).join(",");

  return {
    "data-tally-open": tallyId,
    "data-tally-layout": "modal" as const,
    "data-tally-emoji-text": "👋",
    "data-tally-emoji-animation": "wave" as const,
    "data-tally-auto-close": "0",
    ...(isSignedIn && hiddenFields && { "data-tally-hidden-fields": hiddenFields }),
  };
}

/** Icon-only feedback button for use inside the mobile nav panel. No fixed positioning. */
export function FeedbackButtonIcon({ onPress }: { onPress?: () => void }) {
  const tallyProps = useTallyProps();

  return (
    <button
      {...tallyProps}
      onClick={onPress}
      className="size-14 rounded-full bg-black text-white flex items-center justify-center flex-shrink-0"
      aria-label="Feedback"
    >
      <FeedbackIcon />
    </button>
  );
}

/** Fixed feedback button — hidden on mobile (< lg), shown on desktop. Hidden on admin pages. */
export function FeedbackButton() {
  const tallyProps = useTallyProps();
  const pathname = usePathname();

  if (pathname?.startsWith("/admin") || pathname?.startsWith("/lead")) return null;

  return (
    <>
      <Script
        src="https://tally.so/widgets/embed.js"
        strategy="lazyOnload"
      />
      <button
        {...tallyProps}
        className="hidden lg:flex fixed bottom-4 right-4 z-50 items-center gap-2 rounded-full bg-white px-5 py-3 text-copy text-black shadow-lg transition-all hover:shadow-xl"
      >
        <FeedbackIcon />
        Feedback
      </button>
    </>
  );
}
